import React from "react"
import en from "@/locales/en/common.json"
import pt from "@/locales/pt/common.json"

type SupportedLanguage = "en" | "pt"

type TranslationRecord = typeof en

const TRANSLATIONS: Record<SupportedLanguage, TranslationRecord> = { en, pt }

const DEFAULT_LANGUAGE: SupportedLanguage = "en"
const STORAGE_KEY = "odynx:language"

type TranslationVariables = Record<string, string | number | boolean | null | undefined>

export type TranslationFunction = (key: string, varsOrFallback?: TranslationVariables | string, fallback?: string) => string

type LanguageContextValue = {
  language: SupportedLanguage
  setLanguage: (lang: SupportedLanguage) => void
  availableLanguages: { code: SupportedLanguage; label: string }[]
  t: TranslationFunction
  translations: TranslationRecord
}

const LanguageContext = React.createContext<LanguageContextValue | undefined>(undefined)

const getNestedTranslation = (translationObject: TranslationRecord, key: string) => {
  return key.split(".").reduce<unknown>((accumulator, currentKey) => {
    if (accumulator && typeof accumulator === "object" && currentKey in accumulator) {
      return (accumulator as Record<string, unknown>)[currentKey]
    }
    return undefined
  }, translationObject)
}

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguageState] = React.useState<SupportedLanguage>(DEFAULT_LANGUAGE)

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const storedLanguage = window.localStorage.getItem(STORAGE_KEY)
    if (storedLanguage === "pt" || storedLanguage === "en") {
      setLanguageState(storedLanguage)
    }
  }, [])

  const setLanguage = React.useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, lang)
    }
  }, [])

  const value = React.useMemo<LanguageContextValue>(() => {
    const translate: TranslationFunction = (key, varsOrFallback, fallback) => {
      let replacements: TranslationVariables | undefined
      let explicitFallback: string | undefined

      if (varsOrFallback && typeof varsOrFallback === "object" && !Array.isArray(varsOrFallback)) {
        replacements = varsOrFallback as TranslationVariables
        explicitFallback = fallback
      } else if (typeof varsOrFallback === "string") {
        explicitFallback = varsOrFallback
      }

      const target = getNestedTranslation(TRANSLATIONS[language], key)
      if (typeof target === "string") {
        return applyReplacements(target, replacements)
      }
      const defaultValue = getNestedTranslation(TRANSLATIONS.en, key)
      if (typeof defaultValue === "string") {
        return applyReplacements(defaultValue, replacements)
      }
      if (explicitFallback) {
        return applyReplacements(explicitFallback, replacements)
      }
      return key
    }

    return {
      language,
      setLanguage,
      availableLanguages: [
        { code: "en", label: "English" },
        { code: "pt", label: "Português" }
      ],
      t: translate,
      translations: TRANSLATIONS[language]
    }
  }, [language, setLanguage])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useLanguage = () => {
  const context = React.useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

export const useTranslation = () => {
  const { t } = useLanguage()
  return React.useMemo(() => ({ t }), [t])
}

const applyReplacements = (template: string, replacements?: TranslationVariables) => {
  if (!replacements) {
    return template
  }
  return template.replace(/{{\s*([^{}\s]+)\s*}}/g, (match, token) => {
    if (Object.prototype.hasOwnProperty.call(replacements, token)) {
      const value = replacements[token]
      if (value === null || value === undefined) {
        return ""
      }
      return String(value)
    }
    return match
  })
}
